// Email integrations API — talks to email-service through the gateway
// (/api/v1/email-integrations*). Auth + tenant headers are attached by apiClient.
import { apiClient } from './apiClient';

export type MailboxPurpose = 'documents' | 'leave' | 'payroll' | 'support' | 'hr_general';
export type ProviderKind = 'imap' | 'google_workspace' | 'microsoft_365';
export type IntegrationStatus = 'pending' | 'connected' | 'error' | 'disabled';

/** Safe, secret-free projection returned by every integration API. */
export interface EmailIntegration {
  id: string;
  displayName: string;
  emailAddress: string;
  provider: ProviderKind;
  authType: 'password' | 'app_password' | 'oauth';
  purpose: MailboxPurpose;
  status: IntegrationStatus;
  enabled: boolean;
  lastError?: string | null;
  hasCredentials: boolean;
  imapHost?: string | null;
  imapPort?: number | null;
  pollIntervalSec?: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateImapInput {
  displayName: string;
  emailAddress: string;
  purpose: MailboxPurpose;
  host: string;
  port: number;
  secure: boolean;
  password: string;
  username?: string;
}

const BASE = '/api/v1/email-integrations';

export const listIntegrations = (): Promise<EmailIntegration[]> =>
  apiClient.get<EmailIntegration[]>(BASE).then((r) => r.data);

/** Returns the Google consent URL to redirect the browser to. */
export const startGoogleConnect = (
  purpose: MailboxPurpose,
  displayName: string,
  returnUrl: string,
): Promise<string> =>
  apiClient
    .get<{ url: string }>(`${BASE}/oauth/google/start`, {
      params: { purpose, displayName, returnUrl },
    })
    .then((r) => r.data.url);

/** Returns the Microsoft consent URL to redirect the browser to. */
export const startMicrosoftConnect = (
  purpose: MailboxPurpose,
  displayName: string,
  returnUrl: string,
): Promise<string> =>
  apiClient
    .get<{ url: string }>(`${BASE}/oauth/microsoft/start`, {
      params: { purpose, displayName, returnUrl },
    })
    .then((r) => r.data.url);

export const createImapIntegration = (input: CreateImapInput): Promise<EmailIntegration> =>
  apiClient
    .post<EmailIntegration>(BASE, {
      displayName: input.displayName,
      emailAddress: input.emailAddress,
      provider: 'imap',
      authType: 'app_password',
      purpose: input.purpose,
      imap: {
        host: input.host,
        port: input.port,
        secure: input.secure,
        username: input.username || input.emailAddress,
        password: input.password,
        watchFolder: 'INBOX',
      },
    })
    .then((r) => r.data);

export interface TestResult {
  ok: boolean;
  mailboxes?: string[];
  error?: string;
  code?: string;
}

export const testIntegration = (id: string): Promise<TestResult> =>
  apiClient.post<TestResult>(`${BASE}/${id}/test`).then((r) => r.data);

export const enableIntegration = (id: string): Promise<EmailIntegration> =>
  apiClient.post<EmailIntegration>(`${BASE}/${id}/enable`).then((r) => r.data);

export const disableIntegration = (id: string): Promise<EmailIntegration> =>
  apiClient.post<EmailIntegration>(`${BASE}/${id}/disable`).then((r) => r.data);

export const syncIntegration = (
  id: string,
): Promise<{ integrationId: string; newMessages: number; durationMs: number }> =>
  apiClient.post(`${BASE}/${id}/sync`).then((r) => r.data);

/** Disconnect + permanently remove a mailbox (revokes OAuth, purges mail). */
export const deleteIntegration = (id: string): Promise<void> =>
  apiClient.delete(`${BASE}/${id}`).then(() => undefined);
